/**
 * @file: detail-column.tsx
 *
 * Desktop column 2 — the detail pane. Shows a placeholder when nothing is
 * selected, the feed thread page for a feed selection, or the chat
 * conversation for a chat selection. The chat conversation is mounted via
 * `ChatConversationView` (a navigation-agnostic component) with the resolved
 * handle + room-info callback passed as plain props — NOT by rendering the
 * chat page.
 */

import { Typography } from '@euler/app/components/typography';
import { ChatConversationView } from '@euler/app/flows/chat/components/conversation/ChatConversationView';
import { BakedConversationHandle } from '@euler/app/flows/chat/lib/conversation/handle';
import { FeedThreadView } from '@euler/app/flows/feed/pages/thread/components/FeedThreadView';
import { feedThreadTitle } from '@euler/app/flows/feed/utils/feedThreadTitle';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MessagesSquare } from 'lucide-react-native';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { InboxThreadItem } from '../../types';

export const DetailColumn: FC<{
  activeThread: InboxThreadItem | null;
  onOpenRoomInfo: () => void;
  onConversationHandleMaterialized: (handle: BakedConversationHandle) => void;
  /** Deselect the pane after the viewer deletes a closed (removed/dismissed)
   * thread from the conversation's banner. */
  onExitConversation: () => void;
}> = memo(
  ({
    activeThread,
    onOpenRoomInfo,
    onConversationHandleMaterialized,
    onExitConversation,
  }) => {
    return (
      <View style={{ flex: 1, backgroundColor: '#f7f7f7' }}>
        {activeThread?.type === 'conversation' ? (
          // A chat is selected (contact or thread). The pane owns its header, so
          // pass `onOpenRoomInfo`. Keyed by the stable `selectionKey` (not the
          // handle), so a first-send bare→baked materialisation updates in place
          // without remounting; the host swaps the handle via the callback.
          <ChatConversationView
            key={activeThread.selectionKey}
            handle={activeThread.handle}
            onOpenRoomInfo={onOpenRoomInfo}
            onConversationHandleMaterialized={onConversationHandleMaterialized}
            onExit={onExitConversation}
          />
        ) : activeThread?.type === 'feed-thread' ? (
          <FeedThreadView
            key={`feed:${activeThread.thread.id}`}
            threadId={activeThread.thread.id}
            scope={activeThread.thread.scope}
            scopeId={activeThread.thread.scopeId}
            title={feedThreadTitle(activeThread.thread)}
            embedded
          />
        ) : (
          <EmptyDetail />
        )}
      </View>
    );
  },
);

const EmptyDetail: FC = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f7f7f7',
        padding: rpx(40),
      }}
    >
      <View
        style={{
          width: rpx(88),
          height: rpx(88),
          borderRadius: rpx(44),
          backgroundColor: '#eef1f5',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: rpx(20),
        }}
      >
        <MessagesSquare size={rpx(40)} color="#b6bdc7" strokeWidth={1.6} />
      </View>
      <Typography.Text
        size={rpx(16)}
        color="#6b7280"
        style={{ fontFamily: FontFamily.NotoSans.Medium }}
      >
        选择一个会话
      </Typography.Text>
      <Typography.Text mt={rpx(8)} size={rpx(13)} color="#9ca3af">
        从左侧选择通知或私信查看详情
      </Typography.Text>
    </View>
  );
});
